import DOMPurify from "dompurify";
import DashboardService from "@/src/api/DashboardService.js";
import { message } from "ant-design-vue";
import { entityDetailsRoute } from "@/src/constants/pageConstants.js";
import dayjs from "dayjs";
import {
  entityDetailsMessage,
  entityTypeDisplay,
  entityTypes,
  entityTypeToRouteMap,
} from "@/src/constants/entityTypeConstants.js";
import { onboardingSteps } from "@/src/constants/authConstants.js";
import { colorSchema } from "@/src/constants/colorSchema.js";
import { entityMessages } from "@/src/constants/prospectConstants.js";
import { COUNTRIES } from "@/src/constants/countries.js";
import { computed, h } from "vue";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
} from "@ant-design/icons-vue";
import { integrations } from "@/src/constants/settingConstants.js";
import { durationOptions } from "@/src/constants/meetingAgentConstants.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import { postDays, postReactions } from "@/src/constants/campaignConstants.js";
import { activitiesRequiringContent } from "@/src/constants/activityConstants.js";
import { isNumber, isEmpty } from "lodash";
import { bytesInUnit } from "@/src/constants/commonConstants.js";
import MixpanelService from "@/src/services/mixpanel/mixpanel.js";
import { themeIconMap } from "@/src/utils/themeIconMap.js";
import { getThemeIcon } from "@/src/utils/themeUtils.js";
import { datatypeMap } from "@/src/constants/typeConstants.js";
import { themeTypes } from "@/src/constants/themeConstants.js";
import { environments } from "@/src/constants/environmentConstants.js";
import EmailService from "@/src/api/EmailService.js";

export const getInitials = (name) => {
  if (!name || typeof name !== "string") return "";

  const cleanName = name.replace(/[^a-zA-Z\s]/g, "").trim();

  const words = cleanName.split(/\s+/);

  if (words.length === 0 || words[0] === "") return "N/A";
  if (words.length === 1) return words[0].charAt(0).toUpperCase();

  const firstInitial = words[0].charAt(0).toUpperCase();
  const lastInitial = words[words.length - 1].charAt(0).toUpperCase();

  return firstInitial + lastInitial;
};

export const getCasedDisplayName = (entityDisplayName) => {
  try {
    if (!entityDisplayName) return "";

    const lowerName = entityDisplayName.toLowerCase();

    if (
      lowerName.includes("crm task") ||
      lowerName.includes("crm_task") ||
      lowerName === "task" ||
      lowerName === "tasks"
    ) {
      return "Task";
    }

    return entityDisplayName;
  } catch (error) {
    console.error(
      "An error occurred while getting cased display name: ",
      error,
    );
  }
};

export const formatSelectedNames = (selectedNames, maxLength = 30) => {
  try {
    if (!selectedNames || selectedNames.length === 0) return "";

    const stringNames = selectedNames
      .map((name) => {
        if (name === null || name === undefined) return "";
        if (typeof name === "object") {
          return (
            name.entity_name ||
            name.name ||
            name.label ||
            String(name.entity_id || name.id || "")
          );
        }
        return String(name);
      })
      .filter(Boolean);

    const joinedNames = stringNames.join(", ");
    if (joinedNames.length > maxLength) {
      return joinedNames.substring(0, maxLength) + "...";
    }

    return joinedNames;
  } catch (error) {
    console.error("An error occurred while formatting selected names: ", error);
  }
};

export const sanitizeActionName = (name) => {
  try {
    if (!name || typeof name !== "string") {
      return "";
    }
    return name
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_-]/g, "");
  } catch (error) {
    console.error("Error sanitizing action name:", error);
    return "";
  }
};

export function formatDateTime(inputDate, type = "full", timeZone) {
  if (inputDate === null) {
    return null;
  }

  try {
    const date = new Date(inputDate);

    let options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    let dateOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };

    if (timeZone) {
      options.timeZone = timeZone;
      dateOptions.timeZone = timeZone;
    }

    const timeString = date.toLocaleTimeString("en-US", options);
    const dateString = date.toLocaleDateString("en-US", dateOptions);

    const formattedTime = timeString.replace(" ", "");

    switch (type) {
      case "time":
        return formattedTime;
      case "date":
        return dateString;
      case "full":
      default:
        return `${formattedTime}, ${dateString}`;
    }
  } catch (error) {
    console.error("Invalid date format", error);
    return "Invalid date format";
  }
}

export function formatAndConvertDateTime(inputDate, type = "full", timeZone) {
  if (!inputDate || typeof inputDate !== "string") {
    return "Invalid date format";
  }

  try {
    let dateString = inputDate.trim();
    let date;
    let [datePart, timePart] = dateString.split("T");

    if (!datePart || !timePart) {
      throw new Error("Invalid date format");
    }
    timePart = timePart.split(".")[0];
    dateString = datePart + "T" + timePart;
    if (
      !timePart.includes("+") &&
      !timePart.includes("-") &&
      !timePart.includes("Z")
    ) {
      dateString += "Z";
    }

    date = new Date(dateString);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }

    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: timeZone,
    };

    const formattedDateTime = new Intl.DateTimeFormat("en-GB", options).format(
      date,
    );
    switch (type) {
      case "time":
        if (formattedDateTime.includes(", ")) {
          return formattedDateTime.split(", ")[1];
        } else if (formattedDateTime.includes(" at ")) {
          const timeMatch = formattedDateTime.match(/at\s(.+)/);
          return timeMatch ? timeMatch[1] : "";
        }
        return "";
      case "date":
        if (formattedDateTime.includes(", ")) {
          return formattedDateTime.split(", ")[0];
        } else if (formattedDateTime.includes(" at ")) {
          const dateMatch = formattedDateTime.match(/(.+)\s+at\s/);
          return dateMatch ? dateMatch[1] : formattedDateTime.split(" at ")[0];
        }
        return formattedDateTime;
      case "full":
      default:
        return formattedDateTime;
    }
  } catch (error) {
    console.error("Invalid date format", error);
    return "Invalid date format";
  }
}

export function toTitleCase(str) {
  if (!str) return str;

  if (str.toLowerCase() === "crm_tasks" || str.toLowerCase() === "crm_task") {
    return "Task" + (str.toLowerCase().endsWith("s") ? "s" : "");
  }

  return str
    .split(/[_ ]/)
    .map((word) => {
      if (word.toUpperCase() === integrations.LINKEDIN) {
        return "LinkedIn";
      }
      if (word.toUpperCase() === integrations.RB2B) {
        return "RB2B";
      }

      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function getAttributeDisplayName(schema, attributeSlug) {
  if (!attributeSlug) return "";
  if (schema?.attributes) {
    const attr = schema.attributes.find(
      (a) => a.slug === attributeSlug || a.id === attributeSlug,
    );
    if (attr?.name) return attr.name;
  }
  return toTitleCase(attributeSlug);
}

export function getCleanUserName(name) {
  if (!name) return "";
  return name.replace(" (Removed user)", "");
}

export function timeAgo(
  inputDate,
  showSeconds = true,
  currentTime = new Date(),
) {
  const now = new Date(currentTime);
  const past = new Date(inputDate);
  const diffInSeconds = Math.floor((now - past) / 1000);

  const units = [
    { name: "y", seconds: 31536000 },
    { name: "w", seconds: 604800 },
    { name: "d", seconds: 86400 },
    { name: "h", seconds: 3600 },
    { name: "m", seconds: 60 },
    { name: "s", seconds: 1 },
  ];

  if (!showSeconds) {
    units.pop();
  }

  for (const unit of units) {
    const count = Math.floor(diffInSeconds / unit.seconds);
    if (count >= 1) {
      return `${count}${unit.name} ago`;
    }
  }

  return "now";
}

export function formatDescription(description) {
  const rawDescription = description || "Enter Description";
  let result = rawDescription.replace(/\n/g, "<br>");

  result = DOMPurify.sanitize(result);
  result = convertURLsToLinks(result);

  return result;
}

export function validateEmail(email, emptyValid = false) {
  if (emptyValid && (!email || email.trim() === "")) {
    return true;
  }

  if (!email || email.trim() === "") {
    return false;
  }

  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export function decodeHtmlEntities(encodedString) {
  if (encodedString === null || encodedString === undefined) {
    return "";
  }
  const textarea = document.createElement("textarea");
  textarea.innerHTML = encodedString;

  const div = document.createElement("div");
  div.innerHTML = textarea.value;

  div.querySelectorAll("a").forEach((link) => {
    const href = link.getAttribute("href");
    if (href && !href.startsWith("http://") && !href.startsWith("https://")) {
      link.setAttribute("href", `http://${href}`);
    }
    link.style.color = "#2563eb";
    link.style.textDecoration = "underline";
  });

  return div.innerHTML;
}

export function renderContact(contact) {
  if (contact.name && contact.email) {
    return `${contact.name} (${contact.email})`;
  } else if (contact.name) {
    return contact.name;
  } else {
    return contact.email;
  }
}

export function renderLead(lead) {
  if (lead.name && lead.email) {
    return `${lead.name} (${lead.email})`;
  } else if (lead.name) {
    return lead.name;
  } else {
    return lead.email;
  }
}

export function renderCompany(company) {
  if (company.name) {
    return company.name;
  } else {
    return company.website;
  }
}

export const fetchCountries = async (countryOptions, countriesLoading) => {
  countriesLoading.value = true;
  countryOptions.value = COUNTRIES.map(({ name, code }) => ({
    value: name,
    label: name,
    countryCode: code,
    flag: "",
  }));
  countriesLoading.value = false;
};

export const formatValue = (value) => {
  if (typeof value === "number") {
    return value.toLocaleString();
  }
  return value;
};

export const goToURL = (event, url) => {
  event.stopPropagation();
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

export const getCreatedBy = (data) => {
  if (!data) return "";
  // Handle SuperAGI/workflow user (id 0 or -555)
  if (data.id === 0 || data.id === -555) return "SuperAGI";
  if (data.full_name && data.full_name !== " ") return data.full_name;
  else if (data.email && data.email !== "") return data.email;
  else return "";
};

export const formatNumber = (num) => {
  if (num === null || num === undefined || num === 0) {
    return 0;
  }

  if (num < 1000) {
    return num.toFixed(2);
  }

  const units = ["", "K", "M", "B", "T"];
  const order = Math.floor(Math.log10(Math.abs(num)) / 3);
  const unitName = units[order];
  const numShort = num / Math.pow(10, order * 3);

  return `${numShort.toFixed(numShort >= 100 ? 0 : 2)}${unitName}`;
};

export const formatCurrencyNumber = (num, currencyCode) => {
  if (num === null || num === undefined || num === 0) {
    return 0;
  }

  if (num < 1000) {
    return num.toFixed(2);
  }

  const isIndianNumberSystem = ["INR", "PKR", "BDT", "NPR", "LKR"].includes(
    currencyCode,
  );

  if (isIndianNumberSystem) {
    // Indian numbering: K, L (Lakh), Cr (Crore)
    if (num >= 10000000) {
      // 1 Crore = 10 million
      const crores = num / 10000000;
      return `${crores.toFixed(crores >= 100 ? 0 : 2)}Cr`;
    } else if (num >= 100000) {
      // 1 Lakh = 100 thousand
      const lakhs = num / 100000;
      return `${lakhs.toFixed(lakhs >= 100 ? 0 : 2)}L`;
    } else {
      return `${(num / 1000).toFixed(2)}K`;
    }
  }

  const units = ["", "K", "M", "B", "T"];
  const order = Math.floor(Math.log10(Math.abs(num)) / 3);
  const unitName = units[order];
  const numShort = num / Math.pow(10, order * 3);

  return `${numShort.toFixed(numShort >= 100 ? 0 : 2)}${unitName}`;
};

export function formattedConversationsDateTime() {
  const date = new Date();
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const dayName = days[date.getDay()];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");

  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${dayName}, ${day} ${month} ${year} at ${hours}:${minutes} ${ampm}`;
}

export const connectIntegration = async (
  integration,
  source,
  is_team = false,
) => {
  try {
    const response = await DashboardService.connectIntegration(
      integration?.id,
      source,
      null,
      is_team,
    );
    if (response.success) {
      window.open(response.redirect_url, "_blank", "noopener,noreferrer");
    }
  } catch (error) {
    console.error(`Error connecting ${integration.name}:`, error);
    message.error(`An error occurred while connecting ${integration.name}`);
  }
};

export const handleUsernameInitials = () => {
  const userName = localStorage.getItem("userName");
  const userEmail = localStorage.getItem("userEmail");

  if (!userName || userName === "") {
    return userEmail ? userEmail[0] : "";
  }

  return getUsernameInitials(userName);
};

export const getUsernameInitials = (userName) => {
  const nameParts = userName.split(" ");
  return nameParts.length >= 2
    ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
    : userName[0];
};

export function getRouteFromEntity(entity_type) {
  const uppercaseEntityType = entity_type.toUpperCase();
  return entityDetailsRoute[uppercaseEntityType] || "";
}

export const formatContact = (contact) => {
  return contact.name ? `${contact.name} <${contact.email}>` : contact.email;
};

export const parseLastActivity = (value) => {
  if (!value) return null;

  try {
    let activityData = null;

    if (typeof value === "object" && value.value) {
      try {
        activityData = JSON.parse(value.value);
      } catch {
        return value;
      }
    } else if (typeof value === "string") {
      try {
        activityData = JSON.parse(value);
      } catch {
        return value;
      }
    } else if (
      typeof value === "object" &&
      (value.description || value.engagement_type)
    ) {
      activityData = value;
    }

    if (!activityData) return value;

    const description =
      activityData.description || activityData.engagement_type || "";

    if (activityData.created_time) {
      const formattedTime = formatDateTime(activityData.created_time, "full");
      return description ? `${description} - ${formattedTime}` : formattedTime;
    }

    return description || "--";
  } catch (error) {
    console.error("Error parsing last activity:", error);
    return value;
  }
};

export const validateWebsite = (website) => {
  const urlPattern =
    /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
  if (!website) {
    return { isValid: true, error: "" };
  } else if (!urlPattern.test(website)) {
    return { isValid: false, error: "Please enter a valid website URL" };
  } else {
    return { isValid: true, error: "" };
  }
};

export const getUserDisplayName = (user) => {
  if (
    user.first_name &&
    user.last_name &&
    user.first_name !== "" &&
    user.last_name !== ""
  ) {
    return `${user.first_name} ${user.last_name}`;
  }
  if (user.first_name && user.first_name !== "") {
    return user.first_name;
  }
  if (user.name) {
    return user.name;
  }
  if (user.full_name) {
    return user.full_name;
  }
  return user.email;
};

export const getUserDisplayNameWithoutEmail = (user) => {
  if (
    user.first_name &&
    user.last_name &&
    user.first_name !== "" &&
    user.last_name !== ""
  ) {
    return `${user.first_name} ${user.last_name}`;
  }
  if (user.first_name && user.first_name !== "") {
    return user.first_name;
  }
};

export const filterOption = (input, option) => {
  const fullName = option?.label?.toLowerCase();
  return fullName?.includes(input?.toLowerCase());
};

export function generateTimeOptions() {
  const times = [];
  const periods = ["am", "pm"];

  periods.forEach((period) => {
    for (let hour = 0; hour < 12; hour++) {
      for (let minutes = 0; minutes < 60; minutes += 15) {
        let displayHour = hour === 0 ? 12 : hour;
        let displayMinutes = minutes === 0 ? "00" : minutes;
        times.push(`${displayHour}:${displayMinutes}${period}`);
      }
    }
  });

  return times;
}

export function timeToMinutes(timeStr) {
  if (!timeStr) return 0;

  const match = timeStr.match(/^(\d+):(\d+)(am|pm)$/i);
  if (!match) return 0;

  let [, hourStr, minuteStr, period] = match;
  let hours = parseInt(hourStr, 10);
  const minutes = parseInt(minuteStr, 10);

  if (period.toLowerCase() === "pm" && hours !== 12) {
    hours += 12;
  } else if (period.toLowerCase() === "am" && hours === 12) {
    hours = 0;
  }

  return hours * 60 + minutes;
}

export function filterTimeOptions(
  allTimes,
  referenceTime,
  filterType = "after",
) {
  if (!referenceTime || !allTimes?.length) {
    return allTimes || [];
  }

  const referenceMinutes = timeToMinutes(referenceTime);
  const compareFunc =
    filterType === "after"
      ? (timeMinutes) => timeMinutes > referenceMinutes
      : (timeMinutes) => timeMinutes < referenceMinutes;

  return allTimes.filter((time) => compareFunc(timeToMinutes(time)));
}

export function validateTimeRange(startTime, endTime) {
  if (!startTime || !endTime) return null;
  const startMinutes = timeInMinutes(startTime);
  const endMinutes = timeInMinutes(endTime);
  return startMinutes >= endMinutes
    ? "Start time must be before end time"
    : null;
}

export function convertTo24HourFormat(scheduleObj) {
  return Object.entries(scheduleObj).reduce((acc, [day, schedule]) => {
    if (schedule.unavailable) {
      acc[day] = { unavailable: true };
    } else {
      acc[day] = {
        slots: schedule.slots.map((slot) => ({
          start: convertTo24Hour(slot.start),
          end: convertTo24Hour(slot.end),
        })),
      };
    }
    return acc;
  }, {});
}

export function convertTo12HourFormat(scheduleObj) {
  return Object.entries(scheduleObj).reduce((acc, [day, schedule]) => {
    if (schedule.unavailable) {
      acc[day] = { unavailable: true };
    } else {
      acc[day] = {
        slots: schedule.slots.map((slot) => ({
          start: convertTo12Hour(slot.start),
          end: convertTo12Hour(slot.end),
        })),
      };
    }
    return acc;
  }, {});
}

function generateIntervals() {
  const intervals = [];
  let increment = 0.25;
  for (let i = 0; i <= 23.5; i += increment) {
    if (i === 0) {
      intervals.push("0 mins");
    } else if (i < 1) {
      const minutes = i * 60;
      if (minutes === 15 || minutes === 30 || minutes === 45) {
        intervals.push(`${minutes} mins`);
      }
    } else {
      increment = 0.5;
      const hours = Math.floor(i);
      const minutes = (i % 1) * 60;
      if (minutes === 0) {
        intervals.push(`${hours} hr${hours > 1 ? "s" : ""}`);
      } else if (minutes === 30) {
        intervals.push(`${hours}.5 hrs`);
      }
    }
  }
  return intervals;
}

export function timeInMinutes(timeString) {
  const [hours, minutesWithPeriod] = timeString.split(":");
  const minutes = minutesWithPeriod.slice(0, 2);

  let newTime = hours * 60 + parseInt(minutes, 10);
  if (timeString.toLowerCase().includes("pm") && hours !== "12") {
    newTime += 720;
  }
  if (timeString.toLowerCase().includes("am") && hours === "12") {
    newTime -= 720;
  }
  return newTime;
}

export function fromTimeOptions(t) {
  const timeIntervals = generateTimeOptions();
  const now = new Date();
  let currentMinutes = t
    ? timeInMinutes(t)
    : now.getHours() * 60 + now.getMinutes();
  let smallestDifference = Infinity;
  let nearestIndex = 0;
  timeIntervals.forEach((time, index) => {
    const timeMinutes = timeInMinutes(time);
    const difference = timeMinutes - currentMinutes;
    if (difference >= 0 && difference < smallestDifference) {
      smallestDifference = difference;
      nearestIndex = index;
    }
  });
  const fromTime = timeIntervals[nearestIndex];
  const adjustedIntervals = [
    ...timeIntervals.slice(nearestIndex),
    ...timeIntervals.slice(0, nearestIndex),
  ];
  const intervals = generateIntervals();

  let index = -1;
  const newArray = intervals.map((interval, i) => {
    const adjustedIndex = index < 4 ? index + 1 : index + 2;
    index = adjustedIndex;
    return `${adjustedIntervals[adjustedIndex % adjustedIntervals.length]} (${interval})`;
  });
  const toTime = newArray[1].split(" ")[0];
  return [fromTime, toTime, newArray];
}

export function getDate(date) {
  if (date) {
    const formattedDate = dayjs(date).format("YYYY-MM-DD");
    return `${formattedDate}`;
  } else {
    return null;
  }
}

export function getTime(time) {
  if (time) {
    const time24Hour = dayjs(time, "hh:mma").format("HH:mm:ss");
    const formattedTime = dayjs(time24Hour, "HH:mm:ss")
      .local()
      .utc()
      .format("HH:mm:ss");
    return `${formattedTime}`;
  } else {
    return null;
  }
}

export const checkIsEnabled = async (feature) => {
  try {
    const response = await DashboardService.fetchEnabledFeatures();
    const enabled = !!response?.features?.[feature];
    return enabled;
  } catch (error) {
    console.error("Error occurred while fetching enabled features");
  }
};

export const isProspectV2Enabled = () => {
  return localStorage.getItem("prospectV2") === "true";
};

export const copyToClipboard = (text, mes = "Copied to clipboard") => {
  navigator.clipboard.writeText(text).then(
    () => {
      message.success(mes).then();
    },
    (err) => {
      console.error("Could not copy text to clipboard: ", err);
      message.error("Failed to copy text to clipboard").then();
    },
  );
};

export const openRouteInNewTab = (route) => {
  if (route) window.open(route, "_blank");
};

export const copyRouteLink = (route) => {
  if (route) {
    copyToClipboard(window.location.origin + route, "Link copied to clipboard");
  }
};

export const updateFilterOptions = (filtersOptions, id, options) => {
  const filter = filtersOptions.find(
    (filter) => filter.attribute_id === id || filter.id === id,
  );
  if (filter) {
    filter.options = options;
  } else {
    console.error(`Filter with id ${id} not found.`);
  }
};

export const fetchUsersAndUpdateFilters = async (
  filtersOptions,
  filterKey,
  entity = "",
) => {
  try {
    const response = await DashboardService.fetchUsers("delete");
    if (response.success) {
      const users = response.users || [];
      let uniqueUsers = Array.from(
        new Map(users.map((user) => [user.id, user])).values(),
      ).map((user) => ({
        ...user,
        name: user.is_owner_deleted ? `${user.name} (Removed user)` : user.name,
        isDeleted: user.is_owner_deleted,
      }));

      const currentUserId = -1;
      const onlyMeOption = {
        id: currentUserId,
        name: "Only Me",
        isDeleted: false,
      };
      uniqueUsers.unshift(onlyMeOption);

      if (entity === entityTypes.ACTION) {
        uniqueUsers.push({ id: -555, name: "SuperAGI" });
      }

      await checkAndUpdateTimezone(response.timezone);
      updateFilterOptions(filtersOptions, filterKey, uniqueUsers);
    } else {
      console.error("Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

export const checkAndUpdateTimezone = async (timezone) => {
  if (!timezone) {
    let timezone = getTimeZone();
    DashboardService.updateTimezone(timezone).then(async (response) => {
      if (response.success) {
        console.log("Timezone updated successfully");
        const store = (await import("@/src/store/index.js")).default;
        await store.dispatch("workspace/refreshUserTimezone");
      } else {
        console.error("Failed to update timezone");
      }
    });
  }
};

export const fetchUsersAndTeamsForTabbedFilter = async (
  filtersOptions,
  parentFilterKey,
  userTabKey,
  teamTabKey,
) => {
  try {
    const [usersResponse, teamsResponse] = await Promise.all([
      DashboardService.fetchUsers("delete"),
      DashboardService.fetchTeams(),
    ]);

    const parentFilter = filtersOptions.find((f) => f.id === parentFilterKey);
    if (!parentFilter || !parentFilter.tabs) {
      console.error(
        `Filter with id ${parentFilterKey} not found or has no tabs.`,
      );
      return;
    }

    if (usersResponse?.success) {
      const users = usersResponse.users || [];
      let uniqueUsers = Array.from(
        new Map(users.map((user) => [user.id, user])).values(),
      ).map((user) => ({
        ...user,
        name: user.is_owner_deleted ? `${user.name} (Removed user)` : user.name,
        isDeleted: user.is_owner_deleted,
      }));

      const currentUserId = -1;
      const onlyMeOption = {
        id: currentUserId,
        name: "Only Me",
        isDeleted: false,
      };
      uniqueUsers.unshift(onlyMeOption);

      await checkAndUpdateTimezone(usersResponse.timezone);

      const userTab = parentFilter.tabs.find((t) => t.id === userTabKey);
      if (userTab) {
        userTab.options = uniqueUsers;
      }
    }

    if (teamsResponse?.success) {
      const teams = teamsResponse.teams || [];
      const teamOptions = teams.map((team) => ({
        id: `team_${team.id}`,
        name: team.name,
        teamId: team.id,
      }));

      const teamTab = parentFilter.tabs.find((t) => t.id === teamTabKey);
      if (teamTab) {
        teamTab.options = teamOptions;
      }
    }
  } catch (error) {
    console.error("Error fetching users and teams:", error);
  }
};

export function smoothScroll(element, target, duration) {
  const start = element.scrollTop;
  const change = target - start;
  const startTime = performance.now();

  function animateScroll(currentTime) {
    const elapsedTime = currentTime - startTime;
    const progress = Math.min(elapsedTime / duration, 1);
    element.scrollTop = start + change * easeInOutQuad(progress);

    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    }
  }

  function easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  requestAnimationFrame(animateScroll);
}

export const truncateName = (name) => {
  return name.length > 12 ? name.slice(0, 12) + "..." : name;
};

export const getTimeZone = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone === "Asia/Calcutta" ? "Asia/Kolkata" : timezone;
};

export const profileTimezone = async () => {
  try {
    const { default: store } = await import("@/src/store/index.js");
    const timezone = await store.dispatch("workspace/fetchUserTimezone");
    return timezone || getTimeZone();
  } catch (error) {
    return getTimeZone();
  }
};

export const getStepName = (step_type, follow_up) => {
  if (step_type === "EMAIL") {
    return follow_up ? "Follow Up Email" : "Email Outreach";
  } else if (step_type === "LINKEDIN_INMAIL") {
    return "LinkedIn InMail";
  } else if (step_type === "LINKEDIN_MESSAGE") {
    return "LinkedIn Message";
  } else if (step_type === "LINKEDIN_INVITE") {
    return "LinkedIn Connection Request";
  } else if (step_type === "LINKEDIN_POST_REACTION") {
    return "React to LinkedIn Post";
  }
};

export const getOverviewLabel = (options, value) => {
  const option = options.find((option) => option.value === value);
  return option ? option.label : "--";
};

export const isOverviewDefined = (val) => {
  return (
    typeof val !== "undefined" &&
    val !== null &&
    val !== "" &&
    val !== "Invalid Date"
  );
};
export const toGetOnboardingStatus = async () => {
  try {
    const response = await DashboardService.getOnboardingStatus();
    if (response?.success) {
      localStorage.setItem("authOnboardingStatus", response.status);
      return response.status;
    } else {
      if (response.workspace_id) {
        WorkspaceService.setCurrentWorkspaceId(response.workspace_id);

        // Clear old permissions for fresh fetch on reload
        localStorage.removeItem("userPermissions");
        localStorage.removeItem("userRoleId");

        window.location.reload();
      } else {
        localStorage.setItem("authOnboardingStatus", onboardingSteps.WORKSPACE);
        WorkspaceService.removeCurrentWorkspaceId();
        return onboardingSteps.WORKSPACE;
      }
    }
  } catch (error) {
    console.error("Error getting onboarding status:", error);
  }

  return null;
};

export const toUpdateOnboardingStatus = async (status) => {
  try {
    const response = await DashboardService.updateOnboardingStatus(status);
    if (response.success) {
      localStorage.setItem("authOnboardingStatus", status);
    }
  } catch (error) {
    console.error(
      "Error occurred while updating the onboarding status: ",
      error,
    );
    message.error("An error occurred while updating the onboarding status");
  }
};

export const checkIfAuthOnboardingInProgress = () => {
  return (
    localStorage.getItem("authOnboardingStatus") !==
    onboardingSteps.ONBOARDING_COMPLETE
  );
};

export const checkIfOnboardingStatusAvailable = () => {
  return !!localStorage.getItem("authOnboardingStatus");
};

export function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + "b";
  if (bytes < 1048576) return (bytes / 1024).toFixed(2) + "kb";
  return (bytes / 1048576).toFixed(2) + "mb";
}

export function renderEntity(entity) {
  if (entity?.title) {
    return entity.title;
  } else if (entity?.name) {
    return entity.name;
  } else if (entity?.summary) {
    return entity.summary;
  } else if (entity?.first_name || entity?.last_name) {
    return `${entity.first_name || ""} ${entity.last_name || ""}`.trim();
  } else if (entity?.website) {
    return entity.website;
  } else if (entity?.email) {
    return entity.email;
  } else if (entity?.phone_number) {
    return entity.phone_number;
  } else if (entity?.list_name) {
    return entity.list_name;
  } else if (entity && typeof entity !== "object") {
    return entity;
  } else {
    return "";
  }
}

export const getProfileColor = (data) => {
  const updated = renderEntity(data);
  if (!updated) return "profile_default_color";
  let hash = 0;
  for (let i = 0; i < updated.length; i++) {
    hash = (hash * 31 + updated.charCodeAt(i)) >>> 0;
  }
  const colors = Object.values(colorSchema);
  const color = colors[hash % colors.length];
  return color ? `bg-[${color}]` : "profile_default_color";
};

const HEX_COLOR_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
const ENTITY_AVATAR_COLORS = Object.values(colorSchema);

export const getAuthUserProfileColor = (user) => {
  const avatarColor = user?.avatar_color?.trim?.();
  if (avatarColor && HEX_COLOR_REGEX.test(avatarColor)) {
    return { style: { backgroundColor: avatarColor }, class: null };
  }
  if (user?.id) {
    const parsedId = parseInt(user.id);
    if (!isNaN(parsedId)) {
      const color =
        ENTITY_AVATAR_COLORS[parsedId % ENTITY_AVATAR_COLORS.length];
      if (color) return { style: { backgroundColor: color }, class: null };
    }
  }
  const identifier = user?.email ?? user?.name ?? renderEntity(user) ?? "";
  if (identifier) {
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = (hash * 31 + identifier.charCodeAt(i)) >>> 0;
    }
    const color = ENTITY_AVATAR_COLORS[hash % ENTITY_AVATAR_COLORS.length];
    if (color) return { style: { backgroundColor: color }, class: null };
  }
  const fallbackClass = getProfileColor(identifier);
  return { style: null, class: fallbackClass };
};

export const toFetchGetStartedStatus = async () => {
  try {
    const response = await DashboardService.fetchGetStartedStatus();
    if (response?.success) {
      return response?.completed;
    }
  } catch (error) {
    console.error("An error occurred while fetching the get started status");
    return false;
  }
};

export const getStartedRedirection = async (store, router) => {
  const isGetStartedComplete = store.getters["getStarted/isGetStartedComplete"];
  const appStoreVersion =
    window.APP_STORE_CONFIG?.version ||
    store.getters["appStore/appStoreVersion"];
  if (isGetStartedComplete) {
    router.push(appStoreVersion === "v2" ? "/i-assistant" : "/home");
  } else {
    const redirectTo =
      appStoreVersion === "v2" ? "/i-assistant" : "/get_started";
    router.push(redirectTo);
  }
};

export const toGetAllEntityLists = async (source) => {
  let entityLists = [];
  try {
    const response = await DashboardService.getAllEntityLists(source);
    if (response?.success) {
      entityLists = [
        ...response?.private_lists.filter((list) => list.id),
        ...response?.shared_lists.filter((list) => list.id),
      ];
    }
  } catch (error) {
    console.error("Error occurred while fetching the entity lists: ", error);
    entityLists = [];
  }

  return entityLists;
};

export const showSuccessMessage = async (
  router,
  count,
  source,
  list,
  sequence,
) => {
  let data = entityMessages[source];
  let route;
  let messageText = count === 1 ? data.singular : data.plural;
  let type;

  if (sequence) {
    route = entityMessages.sequence.route + sequence.id;
    messageText = messageText + entityMessages.sequence.content + sequence.name;
    type = entityMessages.sequence;
  } else if (list) {
    route = entityMessages.list.route + list.id + "/" + (source || "lead");
    messageText = messageText + entityMessages.list.content + list.name;
    type = entityMessages.list;
  } else {
    route = data.route;
    type = entityMessages[source];
  }

  const key = `success-message-${Date.now()}`;
  message.open({
    key,
    content: () =>
      h(
        "div",
        {
          style: {
            display: "flex",
            alignItems: "center",
          },
        },
        [
          h(CheckCircleFilled, {
            style: {
              color: "#52c41a",
              fontSize: "20px",
              marginRight: "10px",
            },
          }),
          h("span", { class: "text_color" }, [
            `${count} ${messageText} ${entityMessages.description}`,
            h(
              "a",
              {
                class: "text_color",
                href: route,
                onClick: (e) => {
                  e.preventDefault();
                  message.destroy(key);
                  router.push({
                    path: route,
                    query: {
                      showToast: true,
                      entityType: data.page,
                    },
                  });
                },
                style: {
                  textDecoration: "none",
                  fontWeight: "bold",
                  marginLeft: "8px",
                },
              },
              [`View ${type.page}`, h("span", { class: "text_color" }, "→")],
            ),
          ]),
        ],
      ),
    duration: 3,
    class: "custom-success-message",
  });
};

export const showAlreadyExistsMessage = async (
  routerOrOptions,
  source = null,
  entityID = null,
  customRoute = null,
  customMessage = null,
) => {
  let router, actualCustomMessage;

  if (
    routerOrOptions &&
    typeof routerOrOptions === "object" &&
    routerOrOptions.customMessage
  ) {
    actualCustomMessage = routerOrOptions.customMessage;
    router = null;
  } else {
    router = routerOrOptions;
    actualCustomMessage = customMessage;
  }

  if (actualCustomMessage) {
    const lowerMessage = actualCustomMessage.toLowerCase();
    const isInsufficientCredits =
      lowerMessage.includes("insufficient credits") ||
      lowerMessage.includes("not enough credits") ||
      lowerMessage.includes("upgrade your plan");

    if (isInsufficientCredits) {
      const key = `insufficient-credits-${Date.now()}`;
      message.open({
        type: "error",
        key,
        content: () =>
          h(
            "span",
            { style: { display: "flex", alignItems: "center", width: "100%" } },
            [
              h(
                "span",
                {
                  style: {
                    marginRight: "8px",
                    color: "#fff",
                    background: "#ff4d4f",
                    fontSize: "14px",
                    fontWeight: "bold",
                    lineHeight: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                  },
                },
                "×",
              ),
              h("span", { style: { flex: "1" } }, actualCustomMessage),
              h(
                "span",
                {
                  style: {
                    marginLeft: "4px",
                    color: "var(--text-color)",
                    cursor: "pointer",
                    fontSize: "inherit",
                    fontWeight: "bold",
                  },
                  onClick: () => {
                    moveToBilling(false, "v1");
                    message.destroy(key);
                  },
                },
                "Upgrade →",
              ),
            ],
          ),
        duration: 5,
        icon: h("span", { style: { display: "none" } }),
      });
      return;
    } else {
      message.error(actualCustomMessage);
      return;
    }
  }

  if (!source || !entityDetailsMessage[source]) {
    message.error("An error occurred");
    return;
  }

  let data = entityDetailsMessage[source];
  const route = data.route + entityID;
  let messageText =
    actualCustomMessage ||
    `A ${data.page?.toLowerCase()} with this email already exists.`;
  const key = `error-message-${Date.now()}`;

  const messageContent = [
    h(ExclamationCircleFilled, {
      style: {
        color: "#ff4d4f",
        fontSize: "20px",
        marginRight: "10px",
      },
    }),
    h("span", [
      messageText,
      router
        ? h(
            "a",
            {
              href: route,
              onClick: (e) => {
                e.preventDefault();
                message.destroy(key);
                router.push({
                  path: route,
                });
              },
              style: {
                color: "white",
                textDecoration: "none",
                fontWeight: "bold",
                marginLeft: "8px",
              },
            },
            [`View ${data.page}`, h("span", "→")],
          )
        : null,
    ]),
  ];

  message.open({
    key,
    content: () =>
      h(
        "div",
        {
          style: {
            display: "flex",
            alignItems: "center",
          },
        },
        messageContent,
      ),
    duration: 3,
    class: "custom-error-message",
  });
};

export const showExportMessage = async (router, source, name) => {
  let messageText = `${name} ${source} will be exported. You can view your exports here`;

  const key = `success-message-${Date.now()}`;
  message.open({
    key,
    content: () =>
      h(
        "div",
        {
          style: {
            display: "flex",
            alignItems: "center",
          },
        },
        [
          h(CheckCircleFilled, {
            style: {
              color: "#52c41a",
              fontSize: "20px",
              marginRight: "10px",
            },
          }),
          h("span", { class: "text_color" }, [
            messageText,
            h(
              "a",
              {
                href: "/settings#exports",
                class: "text_color",
                onClick: (e) => {
                  e.preventDefault();
                  message.destroy(key);
                  router.push({ path: "/settings", hash: "#exports" });
                },
                style: {
                  textDecoration: "none",
                  fontWeight: "bold",
                  marginLeft: "8px",
                },
              },
              [`View Exports`, h("span", { class: "text_color" }, "→")],
            ),
          ]),
        ],
      ),
    duration: 3,
    class: "custom-success-message",
  });
};

export function isWithinTwoDays(date) {
  if (!date) return false;
  const checkDate = new Date(date);
  const currentDate = new Date();
  const twoDaysInMs = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds
  return currentDate - checkDate < twoDaysInMs;
}

export function convertTo12Hour(time24) {
  const [hours, minutes] = time24.split(":");
  let hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "pm" : "am";
  hour = hour % 12 || 12;
  return `${hour}:${minutes}${ampm}`;
}

export function convertTo24Hour(time12) {
  const [time, modifier] = time12.match(/(\d+:\d+)(am|pm)/i).slice(1);
  let [hours, minutes] = time.split(":");
  let hour = parseInt(hours, 10);
  let minute = parseInt(minutes, 10);

  if (hour === 12) {
    hour = modifier.toLowerCase() === "pm" ? 12 : 0;
  } else if (modifier.toLowerCase() === "pm") {
    hour += 12;
  }
  return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
}

export function formatDuration(minutes) {
  const option = durationOptions.find((opt) => opt.value === minutes);
  return option ? option.label : `${minutes}min`;
}

export function deepCopy(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(deepCopy);
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, deepCopy(value)]),
  );
}

export const disconnectMqtt = async () => {
  try {
    const { realtimeClient } = await import("@/src/services/chats/index.js");
    await realtimeClient.disconnect();
  } catch (error) {
    console.error("Error disconnecting MQTT:", error);
  }
};

export const logout = async () => {
  const baseUrl = window.location.origin;
  const logoutUrl = `${baseUrl}/admin/logout`;

  await disconnectMqtt();

  WorkspaceService.removeCurrentWorkspaceId();
  localStorage.removeItem("userEmail");
  localStorage.removeItem("userName");
  localStorage.removeItem("authOnboardingStatus");
  sessionStorage.removeItem("viewedIntroVideo");
  window.open(logoutUrl, "_self");
};

export const filterDropdown = (input, option) => {
  return option.label.toLowerCase().includes(input.toLowerCase());
};

export const isDomainValid = (domain) => {
  const domainRegex =
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;

  if (!domain || typeof domain !== "string") {
    return false;
  }

  const cleanDomain = domain.trim();

  return !(
    !domainRegex.test(cleanDomain) ||
    cleanDomain.length > 253 ||
    cleanDomain.split(".").pop().length < 2 ||
    cleanDomain.startsWith("www.")
  );
};

export const noop = () => {};

export const getReaction = (reactionType) => {
  const reaction = postReactions.find(
    (r) => r.label.toLowerCase() === reactionType?.toLowerCase(),
  );
  return reaction?.emoji + "  " + reaction?.label;
};

export const getDays = (days) => {
  const reaction = postDays.find((r) => r.value === days);
  return reaction?.label;
};

export const isContact = (type) => {
  return type === entityTypes.CONTACT;
};

export const isLead = (type) => {
  return type === entityTypes.LEAD;
};

export const formatPerson = (person) => {
  const name = [];
  if (person["first_name"] && person["first_name"].trim() !== "") {
    name.push(person["first_name"]);
  }
  if (person["last_name"] && person["last_name"].trim() !== "") {
    name.push(person["last_name"]);
  }
  if (name.length > 0) {
    return name.join(" ");
  } else {
    if (person["email"]) {
      return person["email"]
        .split("@")[0]
        .split(".")
        .join(" ")
        .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());
    } else {
      return null;
    }
  }
};

export const verifyPhoneNumber = (phoneNumber) => {
  const cleanNumber = phoneNumber.replace(/[^\d+]/g, "");
  const phoneRegex = /^\+?\d{8,}$/;
  return phoneRegex.test(cleanNumber);
};

export const createAnchorHtmlFromUrl = (url) => {
  if (!url.trim()) return "";

  try {
    let href = url.startsWith("www.") ? `https://${url}` : url;
    href = encodeURI(href);
    return `<a class="custom_link" href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  } catch (error) {
    console.error("Error creating anchor tag:", error);
    return url;
  }
};

const addMissingAnchorAttributes = (anchorTag) => {
  let updated = anchorTag;

  if (!updated.includes('target="_blank"')) {
    updated = updated.replace("<a", '<a target="_blank"');
  }

  if (!updated.includes("class=")) {
    updated = updated.replace("<a", '<a class="custom_link"');
  }

  if (!updated.includes("rel=")) {
    updated = updated.replace("<a", '<a rel="noopener noreferrer"');
  }

  return updated;
};

export const convertURLsToLinks = (text) => {
  if (!text) return text;

  try {
    const attributeRegex = /\s([\w-]+)="([^"]*)"/g;
    const urlRegex = /(?<!["'])https?:\/\/[^\s<>"']+|(?<!["'])www\.[^\s<>"']+/g;
    const anchorTagRegex = /<a[^>]*>.*?<\/a>/gi;

    const attributes = [];
    const textWithoutAttributes = text.replace(
      attributeRegex,
      (match, attrName, attrValue) => {
        attributes.push({ name: attrName, value: attrValue });
        return ` ${attrName}="%%ATTR${attributes.length - 1}%%"`;
      },
    );

    const anchorTags = [];
    const textWithoutAnchors = textWithoutAttributes.replace(
      anchorTagRegex,
      (match) => {
        const processedAnchor = addMissingAnchorAttributes(match);
        anchorTags.push(processedAnchor);
        return `%%ANCHOR${anchorTags.length - 1}%%`;
      },
    );

    const processedText = textWithoutAnchors.replace(
      urlRegex,
      createAnchorHtmlFromUrl,
    );

    const textWithAnchors = processedText.replace(
      /%%ANCHOR(\d+)%%/g,
      (match, index) => {
        return anchorTags[index];
      },
    );

    return textWithAnchors.replace(/%%ATTR(\d+)%%/g, (match, index) => {
      const attr = attributes[index];
      return attr.value;
    });
  } catch (error) {
    console.error("Error processing URLs:", error);
    return text;
  }
};

export const getActivitiesWithContent = (activities, activityType = null) => {
  if (!activities?.length) return [];

  return activities.filter((activity) => {
    const requiresContent = activitiesRequiringContent.includes(
      activity.engagement_type,
    );

    if (requiresContent) {
      const hasContent = !isEmpty(activity.engagement_detail);
      return activityType
        ? hasContent && activity.engagement_type === activityType
        : hasContent;
    }

    return activityType ? activity.engagement_type === activityType : true;
  });
};

export const getHighestTaskPriorityEntity = (task) => {
  if (task?.contact_ids?.[0]) {
    return {
      type: entityTypes.CONTACT,
      id: task.contact_ids[0],
      route: entityTypeToRouteMap[entityTypes.CONTACT],
    };
  }

  if (task?.lead_ids?.[0]) {
    return {
      type: entityTypes.LEAD,
      id: task.lead_ids[0],
      route: entityTypeToRouteMap[entityTypes.LEAD],
    };
  }

  if (task?.company_ids?.[0]) {
    return {
      type: entityTypes.COMPANY,
      id: task.company_ids[0],
      route: entityTypeToRouteMap[entityTypes.COMPANY],
    };
  }

  if (task?.deal_ids?.[0]) {
    return {
      type: entityTypes.DEAL,
      id: task.deal_ids[0],
      route: entityTypeToRouteMap[entityTypes.DEAL],
    };
  }

  return {
    type: entityTypes.TASK,
    id: task.id,
    route: entityTypeToRouteMap[entityTypes.TASK],
  };
};

export const isEntityMatchingAutoplayTask = (
  autoplayTaskLinkedEntity,
  entityType,
  routeParamId,
) => {
  try {
    if (!autoplayTaskLinkedEntity) return false;

    const { id, type } = autoplayTaskLinkedEntity;
    return type === entityType && id?.toString() === routeParamId;
  } catch (error) {
    console.error(`Error checking if entity matches autoplay task:`, error);
    return false;
  }
};

export const findTabIndexByTitle = (tabs = [], searchTitle = "") => {
  if (!Array.isArray(tabs) || typeof searchTitle !== "string") {
    return -1;
  }

  return tabs.findIndex(
    (tab) =>
      tab?.title?.toLowerCase().trim() === searchTitle.toLowerCase().trim(),
  );
};

export const formatTime = (seconds) => {
  const units = [
    { label: "yr", value: 31536000 }, // 1 year = 365 days
    { label: "mo", value: 2592000 }, // 1 month = 30 days
    { label: "w", value: 604800 }, // 1 week = 7 days
    { label: "d", value: 86400 }, // 1 day
    { label: "h", value: 3600 }, // 1 hour
    { label: "m", value: 60 }, // 1 minute
    { label: "s", value: 1 }, // 1 second
  ];

  let result = [];
  for (const { label, value } of units) {
    if (seconds >= value) {
      const count = Math.floor(seconds / value);
      result.push(`${count}${label}`);
      seconds %= value;
    }
  }

  return result.length ? result.join(" ") : "0s";
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  // Handle CRM Tasks specifically
  if (
    string.toLowerCase() === "crm task" ||
    string.toLowerCase() === "crm tasks"
  ) {
    return "Task" + (string.toLowerCase().endsWith("s") ? "s" : "");
  }
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const closeDropdown = async ({
  ref,
  onClose,
  setSelected,
  delay = 0,
}) => {
  if (!ref?.value) return;

  return new Promise((resolve) => {
    setTimeout(() => {
      ref.value?.blur();

      if (setSelected) {
        setSelected("");
      }

      if (onClose) {
        onClose();
      }

      resolve();
    }, delay);
  });
};

export const createAntSelectBackspaceDisabler = (selectClass) => {
  if (!selectClass) return null;

  let element = null;
  const handler = (e) => e.key === "Backspace" && e.stopImmediatePropagation();

  const observer = new ResizeObserver(() => {
    try {
      element = document.querySelector(selectClass);
      element?.addEventListener("keydown", handler, { capture: true });
      observer.disconnect();
    } catch (error) {
      console.error("Failed to initialize backspace disabler:", error);
    }
  });

  return {
    observe: () => {
      try {
        observer.observe(document.body);
      } catch (error) {
        console.error("Failed to observe backspace disabler:", error);
      }
    },
    disconnect: () => {
      try {
        element?.removeEventListener("keydown", handler, { capture: true });
        observer.disconnect();
      } catch (error) {
        console.error("Failed to cleanup backspace disabler:", error);
      }
    },
  };
};

export const convertToBytes = (value, unit) => {
  if (!isNumber(value)) return 0;

  const normalizedUnit = unit?.toUpperCase() || "";

  if (!normalizedUnit || !bytesInUnit?.[normalizedUnit]) return 0;

  return value * bytesInUnit[normalizedUnit];
};

export const filterItems = (items, query, options = {}) => {
  try {
    const { searchKey = "name", caseSensitive = false } = options;

    if (!Array.isArray(items) || !items.length) {
      return [];
    }

    const searchTerm = (query || "").trim();

    if (!searchTerm) {
      return [...items];
    }

    return items.filter((item) => {
      const itemValue = item[searchKey];

      if (!itemValue) return false;

      const itemText = caseSensitive
        ? String(itemValue)
        : String(itemValue).toLowerCase();
      const searchText = caseSensitive ? searchTerm : searchTerm.toLowerCase();

      return itemText.includes(searchText);
    });
  } catch (error) {
    console.error("Error in filterItems:", error);
    return [];
  }
};

export const moveToBilling = (creditsPack = false, version = "v0") => {
  const currentWorkspaceId = WorkspaceService.getCurrentWorkspaceId();
  const baseBillingURL =
    process.env.NODE_ENV === "staging"
      ? "https://billing-staging.superagi.com/manage_billing"
      : process.env.NODE_ENV === "production"
        ? window.location.href.includes("in.superagi.com")
          ? "https://billing.in.superagi.com/manage_billing"
          : "https://billing.superagi.com/manage_billing"
        : "http://localhost:8081/manage_billing";

  let billingURL = `${baseBillingURL}?workspace_id=${encodeURIComponent(currentWorkspaceId)}`;

  if (creditsPack) {
    billingURL += `&credits_pack=true`;
  }

  MixpanelService.trackPageView("Billing", {
    Path: billingURL,
    Version: version,
    Timestamp: new Date().toISOString(),
  });

  window.open(billingURL);
};

export const formatRouteName = (name) => {
  if (!name) return "Home";
  let formatted = name.replace(/[_-]/g, " ");

  return formatted
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const formatName = (firstName, lastName) => {
  try {
    const firstNameStr = (firstName || "").trim();
    const lastNameStr = (lastName || "").trim();

    if (firstNameStr || lastNameStr) {
      return [firstNameStr, lastNameStr].filter(Boolean).join(" ");
    }

    return "";
  } catch (error) {
    console.error("Error formatting name:", error);
    return "";
  }
};

export const getEntityKey = (entityType, entityId) =>
  `${entityType}_${entityId}`;

export const formatEntityTypeText = ({
  entityType,
  count = 1,
  includeCount = true,
  titleCase = false,
} = {}) => {
  try {
    const entityInfo =
      entityTypeDisplay[entityType] || entityTypeDisplay.default;
    let form = count === 1 ? entityInfo.singular : entityInfo.plural;

    if (titleCase) {
      form = toTitleCase(form);
    }

    return includeCount ? `${count} ${form}` : form;
  } catch (error) {
    console.error("Error formatting entity type text:", error);
    return includeCount
      ? `${count} item${count !== 1 ? "s" : ""}`
      : `item${count !== 1 ? "s" : ""}`;
  }
};

export const formatRemoveAssociationMessage = ({
  primaryEntity,
  primaryEntityType,
  targetEntity,
  targetEntityType,
}) => {
  try {
    const primaryEntityName =
      (primaryEntity && renderEntity(primaryEntity)) ||
      `This ${formatEntityTypeText({
        entityType: primaryEntityType || "default",
        includeCount: false,
        titleCase: true,
      })}`;

    const hasSpecificTargetName = !!(
      targetEntity && renderEntity(targetEntity)
    );
    const targetEntityName = hasSpecificTargetName
      ? renderEntity(targetEntity)
      : `Selected ${formatEntityTypeText({
          entityType: targetEntityType || "default",
          includeCount: false,
          titleCase: true,
        })}`;

    const indefiniteArticle = hasSpecificTargetName ? "" : "the ";

    if (targetEntityType === entityTypes.SEQUENCE) {
      if (targetEntity?.status === "Active") {
        return `<b>${primaryEntityName}</b> is in an active sequence. Do you want to unenroll them?`;
      } else {
        return `Do you want to unenroll <b>${primaryEntityName}</b> from ${indefiniteArticle}<b>${targetEntityName}</b>?`;
      }
    }

    return `<b>${primaryEntityName}</b> will no longer be associated with ${indefiniteArticle}<b>${targetEntityName}</b>. Do you want to proceed?`;
  } catch (error) {
    console.error("Error formatting remove association message:", error);
    return "This entity will be removed from the selected association. Do you want to proceed?";
  }
};

export const notShowOutOfCreditPopup = computed(() => {
  const currentWorkspaceId = String(WorkspaceService.getCurrentWorkspaceId());
  const workspaceIds = {
    production: [
      "5",
      "7226",
      "6259",
      "6088",
      "6066",
      "8677",
      "5831",
      "6268",
      "6418",
      "6469",
    ],
    staging: ["1247"],
  };

  const ENV = process.env.NODE_ENV;

  return !workspaceIds[ENV]?.includes(currentWorkspaceId) || false;
});

export const formatRemoveAssociationSuccessMessage = ({
  primaryEntity,
  primaryEntityType,
  targetEntity,
  targetEntityType,
}) => {
  try {
    const primaryEntityName =
      (primaryEntity && renderEntity(primaryEntity)) ||
      `This ${formatEntityTypeText({
        entityType: primaryEntityType || "default",
        includeCount: false,
        titleCase: true,
      })}`;

    const hasSpecificTargetName = !!(
      targetEntity && renderEntity(targetEntity)
    );
    const targetEntityName = hasSpecificTargetName
      ? renderEntity(targetEntity)
      : `Selected ${formatEntityTypeText({
          entityType: targetEntityType || "default",
          includeCount: false,
          titleCase: true,
        })}`;

    const indefiniteArticle = hasSpecificTargetName ? "" : "the ";

    if (targetEntityType === entityTypes.SEQUENCE) {
      return `You unenrolled ${primaryEntityName} from ${indefiniteArticle}${targetEntityName}`;
    }

    return `You removed the association with ${indefiniteArticle}${targetEntityName}`;
  } catch (error) {
    console.error("Error formatting success message:", error);
    return "Association removed successfully";
  }
};

export const getCleanPageName = (to) => {
  let pageName;

  if (to.name) {
    pageName = formatRouteName(to.name.toString());
  } else if (to.path) {
    const pathSegments = to.path.split("/").filter((segment) => segment);
    if (pathSegments.length > 0) {
      pageName = formatRouteName(pathSegments[0]);
    } else {
      pageName = "Home";
    }
  } else {
    pageName = "Unknown Page";
  }

  return pageName;
};

export const formatTruncatedNumber = (number, maxDigits) => {
  const numStr = number.toString();
  if (numStr.length <= maxDigits) return numStr;
  return numStr.slice(0, maxDigits) + "...";
};

export const showMeetingLinkInput = computed(() => {
  const currentWorkspaceId = String(WorkspaceService.getCurrentWorkspaceId());
  const workspaceIds = {
    production: ["3922", "6818"],
    staging: ["1251"],
  };

  const ENV = process.env.NODE_ENV;

  return workspaceIds[ENV]?.includes(currentWorkspaceId) || false;
});
export const isValidDomain = (domain) => {
  if (!domain) return true;
  const domainRegex =
    /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
  return domainRegex.test(domain);
};

export const generateThemeImageTag = ({
  icon,
  alt = "",
  imgClass = "",
  theme = themeTypes.LIGHT,
} = {}) => {
  const iconConfig = themeIconMap[icon];

  return `<img data-theme-aware="true"
        data-theme-icon=${JSON.stringify({
          icon: iconConfig.darkIcon,
          lightIcon: iconConfig.lightIcon,
        })}
        alt="${alt}"
        class="${imgClass}"
        src="${getThemeIcon(
          {
            icon: iconConfig.darkIcon,
            lightIcon: iconConfig.lightIcon,
          },
          theme,
        )}" />`;
};

export const sanitizeInput = (input) => {
  if (!input) return "";
  return DOMPurify.sanitize(input.trim(), {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
};
export const transformCustomProperties = (properties) => {
  if (!Array.isArray(properties)) {
    return [];
  }

  return properties.map((prop) => {
    const datatype = (prop.datatype || prop.type || "text").toLowerCase();
    const csvboxType = datatypeMap[datatype] || "text";

    const column = {
      column_name: prop.slug,
      display_label: prop.name,
      matching_keywords: prop.slug,
      type: csvboxType,
      required: prop.is_required || false,
    };

    // Date validation - use CSVBox built-in date type
    if (datatype === "date") {
      column.type = "date";
      column.validators = {
        format: "YYYY-MM-DD",
      };
      column.info_hint = "Format: YYYY-MM-DD (e.g., 2025-09-25)";
    }
    // Phone number - use text type, validation handled by backend
    else if (datatype === "phone_number" || datatype === "phone") {
      column.type = "text";
      column.info_hint =
        "Formats: +{country_code}{number} (e.g., +14155551234, +919876543210) or US format (e.g., (415) 555-7823)";
    }

    // For select type, add validators with dropdown options only if options exist
    if (datatype === "select") {
      if (Array.isArray(prop.options) && prop.options.length > 0) {
        column.type = "list";
        const csvboxOptions = prop.options.map((opt) => ({
          value: String(opt),
          display_label: String(opt),
        }));
        column.validators = { values: csvboxOptions, case_sensitive: false };
      } else {
        column.type = "text";
      }
    }

    // For multi_select type, show available options in info hint
    if (datatype === "multi_select") {
      if (Array.isArray(prop.options) && prop.options.length > 0) {
        column.info_hint = `Enter values separated by commas. Available options: ${prop.options.join(", ")}`;
      } else {
        column.info_hint = "Enter multiple values separated by commas";
      }
    }

    return column;
  });
};

export const limitTimestampsWithEllipsis = (timestamps, limit = 5) => {
  if (!timestamps || !Array.isArray(timestamps) || timestamps.length <= limit) {
    return timestamps || [];
  }

  return timestamps.slice(0, limit);
};

export const truncateEmailMessageHTML = (message, limit = 70) => {
  if (!message) return "";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = message;
  const quotedParts = tempDiv.querySelectorAll(
    ".gmail_quote, .gmail_quote_container",
  );
  quotedParts.forEach((part) => part.remove());
  const plainText = tempDiv.textContent.trim();
  return plainText.length > limit
    ? plainText.substring(0, limit) + "..."
    : plainText;
};

export const showEmailActivityDrawer = async (
  id,
  emailActivityDrawerVisible,
  emailActivityMessage,
  emailActivities,
) => {
  if (!id) return;

  try {
    emailActivityDrawerVisible.value = true;

    const response = await EmailService.fetchEmailActivity(id);

    if (response?.success) {
      emailActivityMessage.value = response?.message || {};
      emailActivities.value = response?.activity || [];
    } else {
      message.error("Failed to fetch email activity");
    }
  } catch (error) {
    message.error("An error occurred while fetching email activity");
    console.error("Error fetching email activity:", error);
  }
};

export const removeTrackingPixels = (content) => {
  if (!content) return "";

  try {
    const removeTrackingImageRegex =
      /<img[^>]*src=['"][^'"]*\/pixel[^'"]*['"][^>]*>/gi;
    return content.replace(removeTrackingImageRegex, "");
  } catch (error) {
    console.error("Error removing tracking pixels:", error);
    return content;
  }
};

export const stripInlineBackgroundStyles = (htmlContent) => {
  if (!htmlContent) return htmlContent;

  return htmlContent.replace(
    /style\s*=\s*(["'])((?:(?!\1).)*?)\1/gi,
    (match, quote, styleContent) => {
      const cleaned = styleContent
        .replace(
          /\s*background(?:-color)?\s*:[^;(]*(?:\([^)]*\)[^;]*)*;?\s*/gi,
          "",
        )
        .trim();
      return cleaned ? `style="${cleaned}"` : "";
    },
  );
};

export const parseSignatureTemplate = (signature, firstName, lastName) => {
  if (!signature) return "";

  let parsedSignature = signature;

  const firstnamePattern =
    /\{\{contact\.firstname\s*\|\s*default:\s*['"]([^'"]*)['"]\s*\}\}/gi;
  parsedSignature = parsedSignature.replace(
    firstnamePattern,
    (match, defaultValue) => {
      return firstName?.trim() || defaultValue;
    },
  );

  const lastnamePattern =
    /\{\{contact\.lastname\s*\|\s*default:\s*['"]([^'"]*)['"]\s*\}\}/gi;
  parsedSignature = parsedSignature.replace(
    lastnamePattern,
    (match, defaultValue) => {
      return lastName?.trim() || defaultValue;
    },
  );

  return parsedSignature;
};

export const getEnvironment = () => {
  try {
    const env = process.env.NODE_ENV || environments.DEVELOPMENT;

    if (Object.values(environments).includes(env)) {
      return env;
    }

    return environments.DEVELOPMENT;
  } catch (error) {
    console.error("An error occurred while getting environment:", error);
    return environments.DEVELOPMENT;
  }
};

export const shareReferralLinkSocial = (platform, referralLink) => {
  const baseUrls = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=Check out SuperSales! Use this link to sign up: ${encodeURIComponent(referralLink)}`,
    twitter: `https://twitter.com/intent/tweet?text=Check out SuperSales!&url=${encodeURIComponent(referralLink)}`,
    linkedin: `https://www.linkedin.com/feed/?shareActive=true&text=Join SuperSales using my referral link: ${encodeURIComponent(referralLink)} and get started today!`,
    whatsapp: `https://api.whatsapp.com/send?text=Join me on SuperSales! Here’s my referral link: ${encodeURIComponent(referralLink)}`,
    email: `mailto:?subject=Join SuperSales&body=Check out SuperSales here using my referral link: ${referralLink}`,
  };

  let shareUrl = baseUrls[platform];

  if (shareUrl) {
    window.open(shareUrl, "_blank");

    if (platform === "email") {
      setTimeout(() => {
        if (!document.hasFocus()) {
          return;
        }
        message.info(
          "We couldn't open your email app. Please ensure that you have configured your system's default email app",
        );
      }, 2000);
    }
  } else {
    console.error("Unsupported platform");
  }
};

export const openUrl = (url) => {
  if (!url) return;

  let finalUrl = url.trim();
  if (!/^https?:\/\//i.test(finalUrl)) {
    finalUrl = `https://${finalUrl}`;
  }

  window.open(finalUrl, "_blank");
};

export const formatHref = (value) => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

export const showDynamicToast = ({
  type = "info",
  text = "",
  actionFunction = null,
  actionText = "",
  duration = 3,
}) => {
  if (!text) {
    console.error("Toast message text is required");
    return;
  }

  const key = `dynamic-toast-${Date.now()}`;

  if (!actionFunction || !actionText) {
    return message[type](text, duration);
  }

  const content = h("span", { class: "text_color" }, [
    text,
    h(
      "span",
      {
        class: "text_color",
        onClick: () => {
          message.destroy(key);
          if (typeof actionFunction === "function") {
            actionFunction();
          }
        },
        style: {
          fontWeight: "bold",
          marginLeft: "8px",
          cursor: "pointer",
        },
      },
      [actionText, h("span", { class: "text_color" }, " →")],
    ),
  ]);

  message[type]({
    key,
    content,
    duration,
  });
};

export const sortByCreatedAt = (items, order = "desc", key = "created_at") => {
  if (!Array.isArray(items) || items.length === 0) {
    return items;
  }

  return [...items].sort((a, b) => {
    const dateA = new Date(a[key]);
    const dateB = new Date(b[key]);

    if (order === "asc") {
      return dateA - dateB;
    }

    return dateB - dateA;
  });
};

export const isEmptyValue = (value) => {
  return (
    value === undefined || value === null || value === "nil" || value === ""
  );
};

export const isPresent = (value) => {
  return value !== null && value !== undefined && value !== "";
};

export const hasValidArrayValue = (arr) => {
  return Array.isArray(arr) && arr.length > 0 && arr.some(isPresent);
};

export const toSnakeCase = (str) => {
  try {
    if (!str) return str;
    return str
      .replace(/([a-z])([A-Z])/g, "$1_$2")
      .replace(/([a-zA-Z])(\d)/g, "$1_$2")
      .replace(/(\d)([a-zA-Z])/g, "$1_$2")
      .toLowerCase();
  } catch (error) {
    console.error("Error converting to snake_case:", error);
    return str;
  }
};

export const toCamelCase = (str) => {
  try {
    if (!str || !str.includes("_")) return str;
    const parts = str.split("_");
    return parts
      .map((part, i) => {
        if (i === 0) return part;
        if (/^\d+$/.test(parts[i - 1])) return part;
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join("");
  } catch (error) {
    console.error("Error converting to camelCase:", error);
    return str;
  }
};

/**
 * Get UTM parameters from the current URL
 * @returns {string} UTM query string (without leading &)
 */
export const getUtmParams = () => {
  const utmKeys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ];
  const params = new URLSearchParams(window.location.search);
  const utmParams = [];

  utmKeys.forEach((key) => {
    const value = params.get(key);
    if (value) {
      utmParams.push(`${key}=${encodeURIComponent(value)}`);
    }
  });

  return utmParams.join("&");
};

/**
 * Append UTM parameters to a URL
 * @param {string} baseUrl - The base URL to append UTM params to
 * @returns {string} URL with UTM parameters appended
 */
export const appendUtmParams = (baseUrl) => {
  const utmString = getUtmParams();
  if (!utmString) return baseUrl;

  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}${utmString}`;
};

export const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

export const stringToScopeId = (value) => {
  if (!value) return null;
  const str = String(value);
  if (/^\d+$/.test(str)) return str;
  let hash = BigInt("14695981039346656037");
  const prime = BigInt("1099511628211");
  for (let i = 0; i < str.length; i++) {
    hash ^= BigInt(str.charCodeAt(i));
    hash = BigInt.asUintN(64, hash * prime);
  }
  return hash.toString();
};

export const autoResizeTextareaHeight = (titleRef) => {
  if (titleRef?.value) {
    titleRef.value.style.height = "auto";
    titleRef.value.style.height = `${titleRef.value.scrollHeight}px`;
  }
};

export const applyTitleDiff = (ydoc, yText, oldStr, newStr) => {
  let start = 0;
  while (start < oldStr.length && start < newStr.length && oldStr[start] === newStr[start]) start++;
  let oldEnd = oldStr.length;
  let newEnd = newStr.length;
  while (oldEnd > start && newEnd > start && oldStr[oldEnd - 1] === newStr[newEnd - 1]) {
    oldEnd--;
    newEnd--;
  }
  ydoc.transact(() => {
    if (oldEnd > start) yText.delete(start, oldEnd - start);
    if (newEnd > start) yText.insert(start, newStr.slice(start, newEnd));
  });
};

export const formatNoteTitle = (value) => {
  if (value === "Untitled Note") return "";
  return String(value || "");
};
